import { useEffect, useState } from "react";
import { Loader2, KeyRound, User, Check } from "lucide-react";
import { toast } from "sonner";
import { listUsers, changeUserPassword } from "../lib/api";

export const Recruiters = ({ me }) => {
  const [users, setUsers] = useState(null);
  const [editing, setEditing] = useState(null); // user id
  const [newPw, setNewPw] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listUsers().then(({ data }) => setUsers(data)).catch(() => setUsers([]));
  }, []);

  const save = async (userId) => {
    if (newPw.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      await changeUserPassword(userId, newPw);
      toast.success("Password updated — the recruiter will need to sign in again");
      setEditing(null);
      setNewPw("");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.toString() || "Could not update password");
    } finally {
      setBusy(false);
    }
  };

  const changeMine = async () => {
    if (newPw.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      await changeUserPassword(me.id, newPw);
      toast.success("Your password was updated");
      setEditing(null);
      setNewPw("");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.toString() || "Could not update password");
    } finally {
      setBusy(false);
    }
  };

  if (users === null) return <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-500" /></div>;

  return (
    <div data-testid="recruiters-page" className="space-y-4">
      <div className="bg-[#13161C] border border-[#262C38] rounded-md p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-slate-400" />
            <h3 className="font-heading font-bold uppercase tracking-wide text-white">Your admin account</h3>
          </div>
          <button
            data-testid="change-my-password-btn"
            onClick={() => { setEditing(editing === me.id ? null : me.id); setNewPw(""); }}
            className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wide border border-[#384152] hover:border-[#F95722] text-white px-3 py-1.5 rounded"
          >
            <KeyRound className="h-3.5 w-3.5" /> Change my password
          </button>
        </div>
        <p className="font-mono text-xs text-slate-400">{me.email}</p>
        {editing === me.id && (
          <div className="flex gap-2 mt-3">
            <input
              data-testid="my-new-password-input"
              type="password"
              autoComplete="new-password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              placeholder="New password (min 8 chars)"
              className="flex-1 bg-[#0B0C0E] border border-[#262C38] rounded px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#F95722]"
            />
            <button
              data-testid="save-my-password-btn"
              onClick={changeMine}
              disabled={busy || newPw.length < 8}
              className="inline-flex items-center gap-1 bg-[#F95722] hover:bg-[#FF6B38] text-white font-bold px-3 py-2 rounded text-xs uppercase disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save
            </button>
          </div>
        )}
      </div>

      <div className="bg-[#13161C] border border-[#262C38] rounded-md overflow-hidden">
        <div className="px-4 py-3 border-b border-[#262C38] font-heading font-bold uppercase tracking-wide text-white">
          Recruiter accounts ({users.length})
        </div>
        <ul>
          {users.map((u) => (
            <li key={u.id} data-testid={`recruiter-row-${u.id}`} className="px-4 py-3 border-t border-[#262C38] first:border-t-0">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-white font-bold">{u.name}</div>
                  <div className="font-mono text-xs text-slate-500 truncate">{u.email}</div>
                  {u.setter_names?.length > 0 && (
                    <div className="font-mono text-[10px] text-slate-500 mt-1">
                      Setter names: {u.setter_names.join(", ")}
                    </div>
                  )}
                </div>
                <button
                  data-testid={`reset-password-btn-${u.id}`}
                  onClick={() => { setEditing(editing === u.id ? null : u.id); setNewPw(""); }}
                  className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wide border border-[#384152] hover:border-[#F95722] text-white px-3 py-1.5 rounded shrink-0"
                >
                  <KeyRound className="h-3.5 w-3.5" /> Reset password
                </button>
              </div>
              {editing === u.id && (
                <div className="flex gap-2 mt-3">
                  <input
                    data-testid={`new-password-input-${u.id}`}
                    type="password"
                    autoComplete="new-password"
                    value={newPw}
                    onChange={(e) => setNewPw(e.target.value)}
                    placeholder="New password (min 8 chars)"
                    className="flex-1 bg-[#0B0C0E] border border-[#262C38] rounded px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#F95722]"
                  />
                  <button
                    data-testid={`save-password-btn-${u.id}`}
                    onClick={() => save(u.id)}
                    disabled={busy || newPw.length < 8}
                    className="inline-flex items-center gap-1 bg-[#F95722] hover:bg-[#FF6B38] text-white font-bold px-3 py-2 rounded text-xs uppercase disabled:opacity-50"
                  >
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
